/**
 * useWorkOrderPriorities Hook
 *
 * React Query hook for fetching distinct priority options for the work orders filter.
 */

import { queryKeys } from '@/lib/query-client';
import { useQuery } from '@tanstack/react-query';
import { getJobPriorities } from '../api/prioritiesApi';

/**
 * Fetch distinct priority values for the work orders priority filter dropdown.
 *
 * Returns priorities sourced from actual job data rather than a static list,
 * so the dropdown only shows priorities that exist in the database.
 */
export function useWorkOrderPriorities() {
  const queryKey = queryKeys.jobs.priorities();

  const query = useQuery({
    queryKey,
    queryFn: () => getJobPriorities(),
    staleTime: 5 * 60 * 1000, // 5 minutes - priorities don't change often
    gcTime: 30 * 60 * 1000, // 30 minutes
  });

  // Return data directly — the lookup table always has valid entries
  const data = query.data;

  return {
    data,
    isLoading: query.isLoading,
    isPending: query.isPending,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    status: query.status,
  };
}
